import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getToken, getUser } from './utils/authUtil';

// Admin pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import StudentProfile from './pages/StudentProfile';
import AddStudent from './pages/AddStudent';
import Batches from './pages/Batches';
import QuestionBank from './pages/QuestionBank';
import Tests from './pages/Tests';
import TestCreateWizard from './pages/TestCreateWizard';
import TestDetail from './pages/TestDetail';
import AdminAnalytics from './pages/admin/Analytics';

import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminQuestionBank from './pages/SuperAdminQuestionBank';
import EditStudent from './pages/EditStudent';
import TopicsManager from './pages/TopicsManager';

// Student pages
import StudentPortal from './pages/StudentPortal';

import StudentTests from './pages/StudentTests';
import StudentAnalytics from './pages/StudentAnalytics';
import StudentAI from './pages/StudentAI';
import StudentProfilePage from './pages/StudentProfilePage';
import TakeTest from './pages/TakeTest';
import MockTestSetup from './pages/MockTest';
import MockTestTake from './pages/MockTestTake';
import MistakeBank from './pages/MistakeBank';
import DiagnosticAnalysis from './pages/DiagnosticAnalysis';
import DiagnosticReport from './pages/DiagnosticReport';


import Layout from './components/Layout';
import { PopupProvider } from './context/PopupContext';
import GlobalPopup from './components/GlobalPopup';

const PrivateRoute = ({ children, allowedRole }) => {
  const token = getToken();
  const user = getUser();
  
  if (!token) {
    return <Navigate to="/" />;
  }
  
  if (allowedRole) {
    const roles = Array.isArray(allowedRole) ? allowedRole : [allowedRole];
    const currentRole = user?.realRole || user?.role;

    // Always allow super admins
    if (currentRole === 'super_admin') {
      return children;
    }

    // Check for role match or mapped admin roles
    const isAllowed = roles.includes(currentRole) || 
                     (roles.includes('admin') && (currentRole === 'college_admin' || currentRole === 'admin'));

    if (!isAllowed) {
      return <Navigate to="/" />;
    }
  }
  return children;
};

function App() {
  return (
    <PopupProvider>
      <GlobalPopup />
      <Router>
      <Routes>

        {/* Public */}
        <Route path="/" element={<Login />} />

        {/* Admin routes */}
        <Route path="/admin/dashboard"
          element={
            <PrivateRoute allowedRole="admin">
              <Layout role="admin"><Dashboard /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/students"
          element={
            <PrivateRoute allowedRole="admin">
              <Layout role="admin"><Students /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/students/add"
          element={
            <PrivateRoute allowedRole="admin">
              <Layout role="admin"><AddStudent /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/students/:id"
          element={
            <PrivateRoute allowedRole="admin">
              <Layout role="admin"><StudentProfile /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/students/edit/:id"
          element={
            <PrivateRoute allowedRole="admin">
              <Layout role="admin"><EditStudent /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/batches"
          element={
            <PrivateRoute allowedRole="admin">
              <Layout role="admin"><Batches /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/questions"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <Layout role={getUser()?.role === 'super_admin' ? 'super_admin' : 'admin'}><QuestionBank /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/topics"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <Layout role={getUser()?.role === 'super_admin' ? 'super_admin' : 'admin'}><TopicsManager /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/tests"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <Layout role={getUser()?.realRole === 'super_admin' ? 'super_admin' : 'admin'}><Tests /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/tests/create"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <Layout role={getUser()?.realRole === 'super_admin' ? 'super_admin' : 'admin'}><TestCreateWizard /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/tests/edit/:id"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <Layout role={getUser()?.realRole === 'super_admin' ? 'super_admin' : 'admin'}><TestCreateWizard /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/tests/:id"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <Layout role={getUser()?.realRole === 'super_admin' ? 'super_admin' : 'admin'}><TestDetail /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/analytics"
          element={
            <PrivateRoute allowedRole="admin">
              <Layout role="admin"><AdminAnalytics /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/diagnostic-analysis"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <Layout role={getUser()?.realRole === 'super_admin' || getUser()?.role === 'super_admin' ? 'super_admin' : 'admin'}><DiagnosticAnalysis /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/admin/diagnostic/report/:attemptId"
          element={
            <PrivateRoute allowedRole={['admin', 'super_admin']}>
              <Layout role={getUser()?.realRole === 'super_admin' || getUser()?.role === 'super_admin' ? 'super_admin' : 'admin'}><DiagnosticReport /></Layout>
            </PrivateRoute>
          } 
        />

        <Route path="/super-admin"
          element={
            <PrivateRoute allowedRole="super_admin">
              <Layout role="super_admin"><SuperAdminDashboard /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/super-admin/questions"
          element={
            <PrivateRoute allowedRole="super_admin">
              <Layout role="super_admin"><SuperAdminQuestionBank /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/super-admin/topics"
          element={
            <PrivateRoute allowedRole="super_admin">
              <Layout role="super_admin"><TopicsManager /></Layout>
            </PrivateRoute>
          } 
        />

        {/* Student routes */}
        <Route path="/student/dashboard"
          element={
            <PrivateRoute allowedRole="student">
              <Layout role="student"><StudentPortal /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/student/tests"
          element={
            <PrivateRoute allowedRole="student">
              <Layout role="student"><StudentTests /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/student/analytics"
          element={
            <PrivateRoute allowedRole="student">
              <Layout role="student"><StudentAnalytics /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/student/ai"
          element={
            <PrivateRoute allowedRole="student">
              <Layout role="student"><StudentAI /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/student/profile"
          element={
            <PrivateRoute allowedRole="student">
              <Layout role="student"><StudentProfilePage /></Layout>
            </PrivateRoute>
          } 
        />
        
        <Route path="/student/test/:id"
          element={<PrivateRoute allowedRole="student"><TakeTest /></PrivateRoute>} />
        
        <Route path="/student/mock"
          element={
            <PrivateRoute allowedRole="student">
              <Layout role="student"><MockTestSetup /></Layout>
            </PrivateRoute>
          } 
        />
        <Route path="/student/mock/test"
          element={<PrivateRoute allowedRole="student"><MockTestTake /></PrivateRoute>} />
        
        <Route path="/student/mistakes"
          element={
            <PrivateRoute allowedRole="student">
              <Layout role="student"><MistakeBank /></Layout>
            </PrivateRoute>
          } 
        />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" />} />

      </Routes>
    </Router>
    </PopupProvider>
  );
}

export default App;